import axios from "axios";
import { useEffect, useState } from "react";
import ApiUrl from "../Environment/ApiUrl";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import "../../assets/css/Loader.css";
import { useToast } from "../Toast/ToastContext";

function BluePageBalance() {
  const [activeTab, setActiveTab] = useState("sales-balance");
  const toast = useToast();
  const handleTabClick = (tab) => { setActiveTab(tab); setCurrentPage(1); };

  const [retailerData, setRetailerData] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => { fetchDataRetailer(); fetchDataSales(); }, []);

  const fetchDataRetailer = async () => {
    setError(null);
    try {
      setLoading(true);
      const response = await axios.get(`${ApiUrl}/Api/retailer_details.php`);
      setRetailerData(response.data.data || []);
      setTimeout(() => setLoading(false), 1000);
    } catch (error) {
      console.error("Error fetching retailer data:", error.message);
      setError("Failed to fetch retailer data.");
      setRetailerData([]);
    }
  };

  const fetchDataSales = async () => {
    setError(null);
    try {
      setLoading(true);
      const response = await axios.get(`${ApiUrl}/Api/sales.php`);
      setSalesData(response.data.data || []);
      setTimeout(() => setLoading(false), 1000);
    } catch (error) {
      console.error("Error fetching sales data:", error.message);
      setError("Failed to fetch sales data.");
      setSalesData([]);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
    } catch { return dateString; }
  };

  // ── Return-type aware formatters ──────────────────────────────────────────
  const fmtTotal = (item) =>
    item.return_type === "Amount"
      ? `₹${parseFloat(item.amount || 0).toFixed(2)}`
      : `${parseFloat(item.total_pure || 0).toFixed(3)} g`;

  const fmtPaid = (item) =>
    item.return_type === "Amount"
      ? `₹${parseFloat(item.enter_amount || 0).toFixed(2)}`
      : `${parseFloat(item.pure || 0).toFixed(3)} g`;

  const fmtBalance = (item) =>
    item.return_type === "Amount"
      ? `₹${parseFloat(item.close || 0).toFixed(2)}`
      : `${parseFloat(item.close || 0).toFixed(3)} g`;

  // ── Filtered data ─────────────────────────────────────────────────────────
  const filteredRetailerData = retailerData.filter((item) =>
    item.retailer_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.supplier_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.retailer_date?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.return_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (item.retailer_details?.name || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredSalesData = salesData.filter((item) =>
    item.balance !== "0" && item.balance !== "0.00" && (
      item.sales_no?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      formatDate(item.sales_date)?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.customer_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.item_list[0]?.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.item_list[0]?.item?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const dataToDisplay = activeTab === "retailers-balance" ? filteredRetailerData : filteredSalesData;
  const totalPages = Math.ceil(dataToDisplay.length / pageSize);
  const paginatedData = dataToDisplay.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handlePageChange = (page) => { if (page >= 1 && page <= totalPages) setCurrentPage(page); };

  // ── Separate totals by return_type ────────────────────────────────────────
  const amountRows = filteredRetailerData.filter((r) => r.return_type === "Amount");
  const pureRows   = filteredRetailerData.filter((r) => r.return_type === "Pure");

  const totalAmtValue   = amountRows.reduce((s, r) => s + parseFloat(r.amount       || 0), 0).toFixed(2);
  const totalAmtPaid    = amountRows.reduce((s, r) => s + parseFloat(r.enter_amount || 0), 0).toFixed(2);
  const totalAmtBalance = amountRows.reduce((s, r) => s + parseFloat(r.close        || 0), 0).toFixed(2);
  const totalPureWt     = pureRows.reduce((s, r)   => s + parseFloat(r.total_pure   || 0), 0).toFixed(3);
  const totalPureRet    = pureRows.reduce((s, r)   => s + parseFloat(r.pure         || 0), 0).toFixed(3);
  const totalPureBal    = pureRows.reduce((s, r)   => s + parseFloat(r.close        || 0), 0).toFixed(3);

  // ── Export helpers ────────────────────────────────────────────────────────
  const exportHeaders = [
    "S.No", "Retailer No", "Retailer ID", "Date", "Retailer Name",
    "Items", "Return Type", "Total / Total Pure", "Paid / Returned", "Balance",
  ];
  const exportRows = (data) =>
    data.map((item, i) => [
      i + 1,
      item.retailer_id || "",
      item.supplier_id || "",
      formatDate(item.retailer_date),
      item.retailer_details?.name || "-",
      item.items?.length || 0,
      item.return_type || "",
      fmtTotal(item),
      fmtPaid(item),
      fmtBalance(item),
    ]);

  const renderPageNumbers = () => {
    const pages = [];
    const max = 5;
    let start = Math.max(1, currentPage - Math.floor(max / 2));
    let end = Math.min(totalPages, start + max - 1);
    if (end - start + 1 < max) start = Math.max(1, end - max + 1);
    if (start > 1) {
      pages.push(<li key={1} className={`page-item ${currentPage===1?"active":""}`}><button className="page-link" onClick={() => handlePageChange(1)}>1</button></li>);
      if (start > 2) pages.push(<li key="s…" className="page-item disabled"><span className="page-link">…</span></li>);
    }
    for (let i = start; i <= end; i++)
      pages.push(<li key={i} className={`page-item ${currentPage===i?"active":""}`}><button className="page-link" onClick={() => handlePageChange(i)}>{i}</button></li>);
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push(<li key="e…" className="page-item disabled"><span className="page-link">…</span></li>);
      pages.push(<li key={totalPages} className={`page-item ${currentPage===totalPages?"active":""}`}><button className="page-link" onClick={() => handlePageChange(totalPages)}>{totalPages}</button></li>);
    }
    return pages;
  };

  // ── Print ─────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    let title, tableHtml;
    if (activeTab === "retailers-balance") {
      title = "Retailers Balance Report";
      tableHtml = `<h2>${title}</h2>
        <table style="width:100%;border-collapse:collapse">
          <thead><tr style="background:#f2f2f2">
            ${exportHeaders.map(h => `<th style="border:1px solid #ddd;padding:6px">${h}</th>`).join("")}
          </tr></thead>
          <tbody>
            ${exportRows(filteredRetailerData).map(row =>
              `<tr>${row.map(c => `<td style="border:1px solid #ddd;padding:6px">${c}</td>`).join("")}</tr>`
            ).join("")}
          </tbody>
        </table>`;
    } else {
      title = "Sales Balance Report";
      tableHtml = `<h2>${title}</h2>
        <table style="width:100%;border-collapse:collapse">
          <thead><tr style="background:#f2f2f2">
            ${["S.No","Sales ID","Sales Date","Category","Customer ID","Total","Paid","Balance"].map(h=>`<th style="border:1px solid #ddd;padding:6px">${h}</th>`).join("")}
          </tr></thead>
          <tbody>
            ${filteredSalesData.map((item, i) =>
              `<tr><td style="border:1px solid #ddd;padding:6px">${i+1}</td><td style="border:1px solid #ddd;padding:6px">${item.sales_no}</td><td style="border:1px solid #ddd;padding:6px">${formatDate(item.sales_date)}</td><td style="border:1px solid #ddd;padding:6px">${item.item_list[0]?.category||""}</td><td style="border:1px solid #ddd;padding:6px">${item.customer_id}</td><td style="border:1px solid #ddd;padding:6px">${item.total}</td><td style="border:1px solid #ddd;padding:6px">${item.paid}</td><td style="border:1px solid #ddd;padding:6px">${item.balance}</td></tr>`
            ).join("")}
          </tbody>
        </table>`;
    }
    const w = window.open("","_blank");
    w.document.write(`<html><head><title>${title}</title><style>body{font-family:Arial,sans-serif}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ddd;padding:8px;text-align:left}th{background:#f2f2f2}</style></head><body>${tableHtml}</body></html>`);
    w.document.close(); w.focus(); w.print(); w.close();
    toast.success("Print dialog opened!", 2000, "topRight");
  };

  const exportPDF = () => {
    try {
      const doc = new jsPDF({ orientation: "landscape" });
      if (activeTab === "retailers-balance") {
        doc.text("Retailers Balance Report", 14, 10);
        autoTable(doc, { head: [exportHeaders], body: exportRows(filteredRetailerData), startY: 20, styles: { fontSize: 7 } });
        doc.save("retailers_balance_report.pdf");
      } else {
        doc.text("Sales Balance Report", 14, 10);
        autoTable(doc, {
          head: [["S.No","Sales ID","Sales Date","Category","Customer ID","Total","Paid","Balance"]],
          body: filteredSalesData.map((item, i) => [i+1, item.sales_no, formatDate(item.sales_date), item.item_list[0]?.category, item.customer_id, item.total, item.paid, item.balance]),
          startY: 20,
        });
        doc.save("sales_balance_report.pdf");
      }
      toast.success("PDF exported successfully!", 3000, "topRight");
    } catch { toast.error("Failed to export PDF", 3000, "topRight"); }
  };

  const exportCSV = () => {
    let headers, rows, filename;
    if (activeTab === "retailers-balance") {
      headers = exportHeaders; rows = exportRows(filteredRetailerData); filename = "retailers_balance_report.csv";
    } else {
      headers = ["S.No","Sales ID","Sales Date","Category","Customer ID","Total","Paid","Balance"];
      rows = filteredSalesData.map((item, i) => [i+1, item.sales_no, formatDate(item.sales_date), item.item_list[0]?.category, item.customer_id, item.total, item.paid, item.balance]);
      filename = "sales_balance_report.csv";
    }
    const csv = [headers.join(","), ...rows.map(r => r.map(f=>`"${f}"`).join(","))].join("\n");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([csv], {type:"text/csv;charset=utf-8;"}));
    link.download = filename;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    toast.success("CSV exported successfully!", 3000, "topRight");
  };

  const exportExcel = () => {
    try {
      let wsData, filename;
      if (activeTab === "retailers-balance") {
        wsData = [exportHeaders, ...exportRows(filteredRetailerData)]; filename = "retailers_balance_report.xlsx";
      } else {
        wsData = [["S.No","Sales ID","Sales Date","Category","Customer ID","Total","Paid","Balance"],
          ...filteredSalesData.map((item, i) => [i+1, item.sales_no, formatDate(item.sales_date), item.item_list[0]?.category, item.customer_id, item.total, item.paid, item.balance])];
        filename = "sales_balance_report.xlsx";
      }
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(wsData), "Balance");
      XLSX.writeFile(wb, filename);
      toast.success("Excel exported successfully!", 3000, "topRight");
    } catch { toast.error("Failed to export Excel", 3000, "topRight"); }
  };

  const copyToClipboard = () => {
    let headers, rows;
    if (activeTab === "retailers-balance") { headers = exportHeaders; rows = exportRows(filteredRetailerData); }
    else {
      headers = ["S.No","Sales ID","Sales Date","Category","Customer ID","Total","Paid","Balance"];
      rows = filteredSalesData.map((item, i) => [i+1, item.sales_no, formatDate(item.sales_date), item.item_list[0]?.category||"", item.customer_id, item.total, item.paid, item.balance]);
    }
    const text = [headers,...rows].map(r=>r.map(f=>`"${f}"`).join("\t")).join("\n");
    navigator.clipboard?.writeText(text)
      .then(() => toast.success("Data copied to clipboard!", 3000, "topRight"))
      .catch(() => { const ta=document.createElement("textarea"); ta.value=text; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); toast.success("Copied!", 2000, "topRight"); });
  };

  return (
    <>
      {loading && <div className="loading-overlay"><div className="loading-message"><div className="loadinggif"></div></div></div>}

      <div className="container-fluid mb-5">
        <h6>Blue Sheet Balance</h6>

        {/* Tabs */}
        <div className="card shadow-sm border-0 rounded-3 mb-2">
          <div className="row p-2">
            <div className="col-auto">
              {[["sales-balance","Sales Balance"],["retailers-balance","Retailers Balance"]].map(([tab, label]) => (
                <button key={tab}
                  className={`btn btn-link text-decoration-none px-3 ms-3 ${activeTab===tab?"text-white border-bottom bg-primary":"text-dark"}`}
                  onClick={() => handleTabClick(tab)}>
                  <span style={{fontSize:"16px"}}>{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="card shadow-sm">
          <div className="card-body">
            {/* Toolbar */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center mb-2 mb-md-0">
                <label className="form-label text-dark me-2 mb-0">Show :</label>
                <select className="form-control" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>
                  {[5,10,20,100,200,500,1000].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="d-flex justify-content-center mb-2 mb-md-0 flex-wrap">
                {[["Print",handlePrint],["PDF",exportPDF],["Copy",copyToClipboard],["CSV",exportCSV],["Excel",exportExcel]].map(([lbl,fn]) => (
                  <button key={lbl} className="btn btn-sm btn-info text-white mx-1 mb-1 mb-md-0" onClick={fn}>{lbl}</button>
                ))}
              </div>
              <div className="search-container w-md-25">
                <input type="text" className="form-control" placeholder="Search" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
              </div>
            </div>

            <div className="card tableCard p-2">
              {loading ? <div className="text-center">Loading data...</div>
               : error ? <div className="text-center text-danger">{error}</div>
               : paginatedData.length === 0 ? <div className="text-center">No data available.</div>
               : activeTab === "retailers-balance" ? (
                <div className="table-responsive">
                  <table className="table table-striped table-hover w-100">
                    <thead className="bg-light">
                      <tr>
                        <th>S.No</th>
                        <th>Retailer No</th>
                        <th>Retailer ID</th>
                        <th>Date</th>
                        <th>Retailer Name</th>
                        <th>Items</th>
                        <th>Return Type</th>
                        <th>
                          Total (₹) <span className="text-muted">/</span> Total Pure (g)
                        </th>
                        <th>
                          Paid (₹) <span className="text-muted">/</span> Returned (g)
                        </th>
                        <th>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedData.map((item, index) => (
                        <tr key={item.retailer_id + index}>
                          <td>{(currentPage - 1) * pageSize + index + 1}</td>
                          <td>{item.retailer_id}</td>
                          <td>{item.supplier_id}</td>
                          <td>{formatDate(item.retailer_date)}</td>
                          <td>{item.retailer_details?.name || "-"}</td>
                          <td>{item.items?.length || 0}</td>
                          <td>
                            <span className={`badge ${item.return_type === "Amount" ? "bg-success" : "bg-primary"}`}>
                              {item.return_type || "-"}
                            </span>
                          </td>
                          <td>{fmtTotal(item)}</td>
                          <td>{fmtPaid(item)}</td>
                          <td>
                            <span className={`fw-semibold ${parseFloat(item.close || 0) > 0 ? "text-danger" : "text-success"}`}>
                              {fmtBalance(item)}
                            </span>
                          </td>
                        </tr>
                      ))}

                      {/* Amount-type total row */}
                      {amountRows.length > 0 && (
                        <tr className="table-success fw-bold">
                          <td colSpan="7" className="text-end">
                            Amount Returns Total ({amountRows.length} records)
                          </td>
                          <td>₹{totalAmtValue}</td>
                          <td>₹{totalAmtPaid}</td>
                          <td>₹{totalAmtBalance}</td>
                        </tr>
                      )}

                      {/* Pure-type total row */}
                      {pureRows.length > 0 && (
                        <tr className="table-primary fw-bold text-white">
                          <td colSpan="7" className="text-end">
                            Pure Returns Total ({pureRows.length} records)
                          </td>
                          <td>{totalPureWt} g</td>
                          <td>{totalPureRet} g</td>
                          <td>{totalPureBal} g</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-hover w-100">
                    <thead className="bg-light">
                      <tr>
                        <th>S.No</th><th>Sales ID</th><th>Sales Date</th>
                        <th>Category</th><th>Item Name</th><th>Customer ID</th>
                        <th>Total</th><th>Paid</th><th>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedData.map((item, index) => (
                        <tr key={item.sales_no}>
                          <td>{(currentPage - 1) * pageSize + index + 1}</td>
                          <td>{item.sales_no}</td>
                          <td>{formatDate(item.sales_date)}</td>
                          <td>{item.item_list[0]?.category || "-"}</td>
                          <td>{item.item_list[0]?.item_name_english
                            ? `${item.item_list[0].item_name_english}${item.item_list[0].item_name_tamil ? " / " + item.item_list[0].item_name_tamil : ""}`
                            : (item.item_list[0]?.item || "-")}</td>
                          <td>{item.customer_id}</td>
                          <td>{item.total}</td>
                          <td>{item.paid}</td>
                          <td>{item.balance}</td>
                        </tr>
                      ))}
                      {paginatedData.length > 0 && (
                        <tr className="table-warning fw-bold">
                          <td colSpan="6" className="text-end">Total</td>
                          <td>₹{filteredSalesData.reduce((s,i)=>s+parseFloat(i.total||0),0).toFixed(2)}</td>
                          <td>₹{filteredSalesData.reduce((s,i)=>s+parseFloat(i.paid||0),0).toFixed(2)}</td>
                          <td>₹{filteredSalesData.reduce((s,i)=>s+parseFloat(i.balance||0),0).toFixed(2)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              <div className="row mt-2">
                <div className="col-lg-6 d-flex align-items-center">
                  {dataToDisplay.length > 0 && (
                    <p className="mb-0">
                      Showing {(currentPage-1)*pageSize+1} to {Math.min(currentPage*pageSize, dataToDisplay.length)} of {dataToDisplay.length} entries
                    </p>
                  )}
                </div>
                <div className="col-lg-6">
                  <nav><ul className="pagination justify-content-end">
                    <li className={`page-item ${currentPage===1?"disabled":""}`}><button className="page-link" onClick={()=>handlePageChange(1)}>««</button></li>
                    <li className={`page-item ${currentPage===1?"disabled":""}`}><button className="page-link" onClick={()=>handlePageChange(currentPage-1)}>«</button></li>
                    {renderPageNumbers()}
                    <li className={`page-item ${currentPage===totalPages?"disabled":""}`}><button className="page-link" onClick={()=>handlePageChange(currentPage+1)}>»</button></li>
                    <li className={`page-item ${currentPage===totalPages?"disabled":""}`}><button className="page-link" onClick={()=>handlePageChange(totalPages)}>»»</button></li>
                  </ul></nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default BluePageBalance;